import React from 'react';
import { useTranslation } from 'react-i18next';
import { Edit, Trash, UserCheck } from 'lucide-react';
import { User, Role, Network } from '../../types';
import { useAuth } from '../../context/AuthContext';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '../ui/table';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { ScrollArea } from '../ui/scroll-area';
import { cn } from '../../utils/utils';

interface UserTableProps {
  users: User[];
  isLoading: boolean;
  onEdit: (user: User) => void;
  onDelete: (userId: number | string) => void;
  onImpersonate: (user: User) => void;
  availableNetworks: Network[];
  sortField: keyof User;
  sortDirection: 'asc' | 'desc';
  onSort: (field: keyof User) => void;
}

export const UserTable: React.FC<UserTableProps> = ({
  users,
  isLoading,
  onEdit,
  onDelete,
  onImpersonate,
  sortField,
  sortDirection,
  onSort
}) => {
  const { t } = useTranslation();
  const { currentUser, isImpersonating } = useAuth();

  // Vérifier si l'utilisateur courant est admin
  const isAdmin = currentUser?.role === 'ADMIN';

  // Fonction pour déterminer si on peut impersonner cet utilisateur
  const canImpersonate = (user: User) => {
    // Ne pas permettre l'auto-impersonation en comparant les emails (plus fiable)
    const isSameUser = user.email === currentUser?.email;
    
    console.log('canImpersonate check:', {
      targetUserEmail: user.email,
      targetUserId: user.id,
      currentUserEmail: currentUser?.email,
      currentUserId: currentUser?.id,
      isSameUser,
      isAdmin,
      isImpersonating
    });
    
    return isAdmin && 
           !isSameUser && // Pas soi-même (comparaison par email)
           !isImpersonating; // Pas déjà en mode impersonation
  };

  const renderSortIcon = (field: keyof User) => {
    if (sortField !== field) {
      return null;
    }
    return (
      <span className="ml-1">
        {sortDirection === 'asc' ? '↑' : '↓'}
      </span>
    );
  };

  const sortableHeader = (field: keyof User, label: string) => (
    <TableHead 
      className={cn(
        "cursor-pointer select-none",
        sortField === field && "text-primary"
      )}
      onClick={() => onSort(field)}
    >
      <div className="flex items-center">
        {label}
        {renderSortIcon(field)}
      </div>
    </TableHead>
  );

  // Badge color based on role
  const getRoleBadgeVariant = (role: string) => {
    switch (role) {
      case 'ADMIN': return "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400";
      case Role.CLIENT: return "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400";
      default: return "bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400";
    }
  };

  return (
    <div className="rounded-md border overflow-hidden h-full">
      <ScrollArea className="h-full">
        <Table>
          <TableHeader>
            <TableRow>
              {sortableHeader('name', t('userManagement.name'))}
              {sortableHeader('email', t('userManagement.email'))}
              {sortableHeader('role', t('userManagement.role'))}
              <TableHead>{t('userManagement.networks')}</TableHead>
              {sortableHeader('createdAt', t('userManagement.createdAt'))}
              <TableHead className="text-right">{t('userManagement.actions')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-8">
                  {t('common.loading')}
                </TableCell>
              </TableRow>
            ) : users.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-8">
                  {t('userManagement.noUsers')}
                </TableCell>
              </TableRow>
            ) : (
              users.map((user) => (
                <TableRow key={user.id} className="hover:bg-muted/50">
                  <TableCell className="font-medium">{user.name}</TableCell>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>
                    <Badge className={getRoleBadgeVariant(user.role)}>
                      {user.role}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {!user.networks || user.networks.length === 0 ? (
                      <span className="text-muted-foreground">{t('userManagement.noNetworks')}</span>
                    ) : (
                      <div className="flex flex-wrap gap-1">
                        {user.networks.map((networkName, index) => (
                          <Badge key={`${networkName}-${index}`} variant="outline" className="whitespace-nowrap">
                            {networkName}
                          </Badge>
                        ))}
                      </div>
                    )}
                  </TableCell>
                  <TableCell>
                    {user.createdAt ? new Date(user.createdAt).toLocaleDateString() : '-'}
                  </TableCell>
                  <TableCell className="text-right space-x-2">
                    {canImpersonate(user) && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onImpersonate(user)}
                        className="h-8 w-8 p-0 text-blue-600 hover:text-blue-700 hover:bg-blue-50 dark:text-blue-400 dark:hover:text-blue-300 dark:hover:bg-blue-900/20"
                        title={t('userManagement.impersonate')}
                      >
                        <UserCheck className="h-4 w-4" />
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onEdit(user)}
                      className="h-8 w-8 p-0"
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onDelete(user.id)}
                      className="h-8 w-8 p-0 text-destructive hover:text-destructive/80 hover:bg-destructive/10"
                    >
                      <Trash className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </ScrollArea>
    </div>
  );
};
